import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { deadlineService } from '@/services/deadlineService';
import {
  Deadline,
  DeadlineCreate,
  DeadlineUpdate,
  DeadlineFilters,
} from '@/types/deadline';

/**
 * Hook to fetch all deadlines with optional filters
 */
export const useDeadlines = (filters?: DeadlineFilters) => {
  return useQuery({
    queryKey: ['deadlines', filters],
    queryFn: () => deadlineService.getDeadlines(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1, // Only retry once
    retryDelay: 1000, // Wait 1 second before retry
  });
};

/**
 * Hook to fetch a single deadline by ID
 */
export const useDeadline = (id: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['deadline', id],
    queryFn: () => deadlineService.getDeadlineById(id),
    enabled: enabled && !!id,
  });
};

/**
 * Hook to create a new deadline
 */
export const useCreateDeadline = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DeadlineCreate) => deadlineService.createDeadline(data),
    onSuccess: () => {
      // Invalidate deadlines query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['deadlines'] });
    },
  });
};

/**
 * Hook to update an existing deadline
 */
export const useUpdateDeadline = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: DeadlineUpdate }) =>
      deadlineService.updateDeadline(id, data),
    onSuccess: (updatedDeadline) => {
      // Invalidate both the list and the specific deadline
      queryClient.invalidateQueries({ queryKey: ['deadlines'] });
      queryClient.invalidateQueries({ queryKey: ['deadline', updatedDeadline.id] });
    },
  });
};

/**
 * Hook to delete a deadline
 */
export const useDeleteDeadline = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deadlineService.deleteDeadline(id),
    onSuccess: () => {
      // Invalidate deadlines query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['deadlines'] });
    },
  });
};

/**
 * Hook to mark a deadline as complete or incomplete
 */
export const useCompleteDeadline = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, completed }: { id: number; completed: boolean }) =>
      deadlineService.markDeadlineComplete(id, completed),
    onSuccess: (updatedDeadline) => {
      // Invalidate both the list and the specific deadline
      queryClient.invalidateQueries({ queryKey: ['deadlines'] });
      queryClient.invalidateQueries({ queryKey: ['deadline', updatedDeadline.id] });
    },
  });
};
